import React, { useState } from 'react';
import { 
  Container, Typography, Paper, Box, Grid, TextField, 
  Button, Breadcrumbs, Link, Snackbar, Alert, MenuItem,
  useTheme
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import MessageIcon from '@mui/icons-material/Message';

const Contact = () => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real app, you would send this data to your backend
    console.log('Form submitted:', formData);
    // Show success message
    setSubmitted(true);
    // Reset form
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };
  
  const handleCloseSnackbar = () => {
    setSubmitted(false);
  };
  
  const subjects = [
    'General Inquiry',
    'Technical Support',
    'Account Issues',
    'Feedback',
    'Partnership Opportunities',
    'Other'
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link 
            component={RouterLink} 
            to="/"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Home
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <EmailIcon sx={{ mr: 0.5, fontSize: 20 }} />
            Contact Us
          </Typography>
        </Breadcrumbs>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 2, height: '100%' }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 4 }}>
              Get in Touch
            </Typography>
            
            <Typography variant="body1" paragraph sx={{ mb: 4 }}>
              Have questions, feedback, or need assistance with Meal Maestro? We'd love to hear from you! Fill out the form and our team will get back to you as soon as possible.
            </Typography>
            
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: theme.palette.primary.main,
                fontWeight: 500,
                mb: 1
              }}>
                <EmailIcon sx={{ mr: 1.5 }} /> Email
              </Typography>
              <Typography variant="body2" sx={{ ml: 4 }}>
                support@mealmaestro.com
              </Typography>
            </Box>
            
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: theme.palette.primary.main,
                fontWeight: 500,
                mb: 1
              }}>
                <LocationOnIcon sx={{ mr: 1.5 }} /> Address
              </Typography>
              <Typography variant="body2" sx={{ ml: 4 }}>
                123 Nutrition Avenue<br />
                Foodville, CA 94123<br />
                United States
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle1" sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: theme.palette.primary.main,
                fontWeight: 500,
                mb: 1
              }}>
                <PhoneIcon sx={{ mr: 1.5 }} /> Phone
              </Typography>
              <Typography variant="body2" sx={{ ml: 4 }}>
                +1 (555) 123-4567
              </Typography>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: 3
            }}>
              <MessageIcon sx={{ mr: 1.5, color: theme.palette.secondary.main }} />
              Send Us a Message
            </Typography>
            
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Your Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    required
                    fullWidth
                    label="Your Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    select
                    required
                    fullWidth
                    label="Subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                  >
                    {subjects.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    multiline
                    rows={6}
                    label="Your Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Please provide as much detail as possible..."
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    endIcon={<SendIcon />}
                    sx={{ 
                      py: 1.5, 
                      px: 4, 
                      mt: 2,
                      bgcolor: theme.palette.secondary.main,
                      '&:hover': {
                        bgcolor: theme.palette.secondary.dark
                      }
                    }}
                  >
                    Send Message
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
      
      <Snackbar open={submitted} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Your message has been sent successfully! We'll get back to you soon.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Contact;
